"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { useWordList } from "@/components/tools/word-games/use-word-list";
import { WordListStatus } from "@/components/tools/word-games/word-list-status";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type BoggleResult,
  type BoggleWord,
  DEFAULT_MIN_LENGTH,
  parseGrid,
  solveBoggle,
  wordScore,
} from "@/lib/tool-kit/word-games/boggle";

/** English strings. A translated content file overrides these through `ui`. */
export const BOGGLE_SOLVER_STRINGS = {
  sizeLabel: "Grid size",
  sizeOption: "{size}×{size}",
  minLabel: "Shortest word",
  minOption: "one:{count} letter|other:{count} letters",
  lettersLabel: "Letters, row by row",
  lettersHint:
    "Type every cell from the top-left, one row after another. Spaces and line breaks are ignored.",
  lettersPlaceholder: "tapr resi nown dlce",
  quLabel: "Treat Q as the Qu cube",
  quHint: "The cube counts as two letters, as in the official rules.",
  tryExample: "Try an example",
  emptyPrompt:
    "Type the letters of your grid to list every word that can be traced through it.",
  invalid: "Use the letters A–Z only.",
  countMismatch: "That makes {have} cells; a {size}×{size} grid needs {need}.",
  loading: "Loading the word list (once per visit)…",
  loadError:
    "Could not load the word list. Check your connection and try again.",
  retry: "Try again",
  gridLabel: "Your grid",
  cellLabel: "Row {row}, column {col}:",
  heading: "Words in the grid",
  statWords: "Words",
  statScore: "Score",
  statLongest: "Longest word",
  pathHint: "Press a word to trace its path on the grid.",
  pathShown: "Path for {word}",
  groupHeading: "one:{count} letter|other:{count} letters",
  groupMeta: "{words}, {points} each",
  groupWords: "one:{count} word|other:{count} words",
  groupPoints: "one:{count} point|other:{count} points",
  noWords: "No words of {min} or more letters can be traced in this grid.",
  copyAll: "Copy all",
  copied: "Copied!",
  rulesNote:
    "Words need to be spelled by touching cells (sideways, up, down or diagonally) without using a cell twice. Scoring: 3–4 letters 1 point, 5 letters 2, 6 letters 3, 7 letters 5, 8 or more 11. Each word counts once.",
  sourceNote:
    "Words come from ENABLE, a public-domain word-game list. It is not the dictionary a game night agrees on, so settle disputed words with your own rules.",
} as const;

type Strings = typeof BOGGLE_SOLVER_STRINGS;

const EXAMPLE = { text: "tapr resi nown dlce", size: 4 };
const SIZES = [4, 5];
const MIN_LENGTHS = [3, 4, 5];
const PATH_FILL = "border-primary bg-primary/20";

function cellText(cell: string): string {
  return cell === "qu" ? "Qu" : cell.toUpperCase();
}

function Grid({
  cells,
  size,
  path,
  strings,
}: {
  cells: readonly string[];
  size: number;
  path: readonly number[];
  strings: Strings;
}) {
  const fmt = useToolNumber();
  return (
    <div className="space-y-2">
      <p className={HINT}>{strings.gridLabel}</p>
      <ul
        className="grid w-fit gap-1.5"
        style={{ gridTemplateColumns: `repeat(${size}, minmax(0, 3rem))` }}
      >
        {cells.map((cell, index) => {
          const step = path.indexOf(index);
          return (
            <li
              className={`relative flex h-12 w-12 items-center justify-center rounded-md border-2 font-bold text-foreground ${step >= 0 ? PATH_FILL : "border-border bg-card"}`}
              // biome-ignore lint/suspicious/noArrayIndexKey: a cell's identity is its position
              key={index}
            >
              <span className="sr-only">
                {t(strings.cellLabel, {
                  row: fmt(Math.floor(index / size) + 1),
                  col: fmt((index % size) + 1),
                })}
              </span>
              {cellText(cell)}
              {step >= 0 ? (
                <span
                  aria-hidden="true"
                  className="absolute end-1 top-0 font-normal text-[10px] text-muted-foreground"
                >
                  {fmt(step + 1)}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function WordGroups({
  result,
  selected,
  onSelect,
  strings,
}: {
  result: BoggleResult;
  selected: string;
  onSelect: (word: string) => void;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const groups = useMemo(() => {
    const byLength = new Map<number, BoggleWord[]>();
    for (const entry of result.words) {
      const group = byLength.get(entry.word.length) ?? [];
      group.push(entry);
      byLength.set(entry.word.length, group);
    }
    return [...byLength].sort((a, b) => b[0] - a[0]);
  }, [result]);
  return (
    <div className="space-y-5">
      {groups.map(([length, entries]) => (
        <section className="space-y-2" key={length}>
          <h3 className={WIDGET_H3}>
            {tPlural(strings.groupHeading, locale, length, {
              count: fmt(length),
            })}{" "}
            <span className={HINT}>
              {t(strings.groupMeta, {
                words: tPlural(strings.groupWords, locale, entries.length, {
                  count: fmt(entries.length),
                }),
                points: tPlural(
                  strings.groupPoints,
                  locale,
                  wordScore(length),
                  { count: fmt(wordScore(length)) }
                ),
              })}
            </span>
          </h3>
          <ul className="flex flex-wrap gap-2">
            {entries.map((entry) => (
              <li key={entry.word}>
                <button
                  aria-pressed={selected === entry.word}
                  className={`rounded-md border px-2.5 py-1 font-mono text-foreground text-sm transition-colors hover:bg-muted ${selected === entry.word ? PATH_FILL : "border-border bg-card"}`}
                  onClick={() =>
                    onSelect(selected === entry.word ? "" : entry.word)
                  }
                  type="button"
                >
                  {entry.word}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export default function BoggleSolverWidget() {
  const strings = useToolUi(BOGGLE_SOLVER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [size, setSize] = useState(4);
  const [minLength, setMinLength] = useState(DEFAULT_MIN_LENGTH);
  const [quCube, setQuCube] = useState(true);
  const [text, setText] = useState("");
  const [selected, setSelected] = useState("");
  const parsed = parseGrid(text, size, quCube);
  const { words, status, retry } = useWordList(parsed.ok);
  const cellsKey = parsed.ok ? parsed.cells.join(",") : "";
  const result = useMemo(() => {
    if (!(words && cellsKey)) {
      return null;
    }
    return solveBoggle(words, cellsKey.split(","), size, minLength);
  }, [words, cellsKey, size, minLength]);
  const pick = result?.words.find((entry) => entry.word === selected);
  const longest = result?.words[0]?.word;

  function changeSize(value: number) {
    setSize(value);
    setMinLength(value > 4 ? 4 : DEFAULT_MIN_LENGTH);
    setSelected("");
  }

  function notice() {
    if (parsed.ok) {
      return null;
    }
    if (parsed.reason === "empty") {
      return <Notice>{strings.emptyPrompt}</Notice>;
    }
    if (parsed.reason === "invalid") {
      return <ErrorNotice>{strings.invalid}</ErrorNotice>;
    }
    return (
      <Notice>
        {t(strings.countMismatch, {
          have: fmt(parsed.have),
          size: fmt(size),
          need: fmt(size * size),
        })}
      </Notice>
    );
  }

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="boggle-size"
          label={strings.sizeLabel}
          onChange={(value) => changeSize(Number(value))}
          options={SIZES.map((value) => ({
            value: String(value),
            label: t(strings.sizeOption, { size: fmt(value) }),
          }))}
          value={String(size)}
        />
        <SelectField
          id="boggle-min"
          label={strings.minLabel}
          onChange={(value) => setMinLength(Number(value))}
          options={MIN_LENGTHS.map((value) => ({
            value: String(value),
            label: tPlural(strings.minOption, locale, value, {
              count: fmt(value),
            }),
          }))}
          value={String(minLength)}
        />
      </div>
      <TextField
        hint={strings.lettersHint}
        id="boggle-letters"
        label={strings.lettersLabel}
        mono
        onChange={(value) => {
          setText(value.slice(0, 120));
          setSelected("");
        }}
        placeholder={strings.lettersPlaceholder}
        value={text}
      />
      <div className="flex flex-wrap items-center gap-4">
        <Checkbox
          checked={quCube}
          label={strings.quLabel}
          onChange={setQuCube}
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setSize(EXAMPLE.size);
            setMinLength(DEFAULT_MIN_LENGTH);
            setText(EXAMPLE.text);
            setSelected("");
          }}
          type="button"
        >
          {strings.tryExample}
        </button>
      </div>
      <p className={HINT}>{strings.quHint}</p>
      {notice()}
      {parsed.ok ? (
        <Grid
          cells={parsed.cells}
          path={pick?.path ?? []}
          size={size}
          strings={strings}
        />
      ) : null}
      <WordListStatus
        error={strings.loadError}
        loading={strings.loading}
        onRetry={retry}
        retry={strings.retry}
        status={status}
      />
      {parsed.ok && result ? (
        <div aria-live="polite" className="space-y-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 className={WIDGET_H2}>{strings.heading}</h2>
            <CopyButton
              copiedLabel={strings.copied}
              disabled={result.words.length === 0}
              label={strings.copyAll}
              small
              text={result.words.map((entry) => entry.word).join("\n")}
            />
          </div>
          {result.words.length === 0 ? (
            <Notice>{t(strings.noWords, { min: fmt(minLength) })}</Notice>
          ) : (
            <>
              <StatGrid>
                <StatCard
                  emphasis
                  label={strings.statWords}
                  value={fmt(result.words.length)}
                />
                <StatCard label={strings.statScore} value={fmt(result.score)} />
                <StatCard label={strings.statLongest} value={longest} />
              </StatGrid>
              <p className={HINT}>
                {pick
                  ? t(strings.pathShown, { word: pick.word })
                  : strings.pathHint}
              </p>
              <WordGroups
                onSelect={setSelected}
                result={result}
                selected={selected}
                strings={strings}
              />
            </>
          )}
        </div>
      ) : null}
      <p className={HINT}>{strings.rulesNote}</p>
      <p className={HINT}>{strings.sourceNote}</p>
    </>
  );
}
